import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { UserRound } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { branchesQuery, membersQuery, useUpdateMember } from '@/api/queries'
import type { Branch, Member } from '@/api/types'
import { useCompanyCaller } from '@/app/company'
import { MemberPhoto, ProfileFields, StandingReadOnly } from '@/app/staff-profile'
import { errorText } from '@/domain/errors'
import { profileChanges, profileProblems, type ProfileProblems } from '@/domain/staff'
import { t } from '@/i18n'
import { Button } from '@/ui/button'
import { InlineBanner } from '@/ui/inline-banner'
import { EmptyState, Page, PageHeader } from '@/ui/page'
import { LoadingBlock } from '@/ui/spinner'

export const Route = createFileRoute('/_app/c/$orgId/profile')({
  component: MyProfile,
})

/// «Мій профіль»: every member's own position, bio and photo — what clients
/// read when they choose a trainer. No staff level is needed (SP3); role and
/// clubs are shown, not changed — they belong to whoever manages the staff.
/// A god from outside has no membership here, so no profile either.
function MyProfile() {
  const { orgId } = Route.useParams()
  const { access } = useCompanyCaller(orgId)
  const member = access.actor !== 'eatlete'
  const members = useQuery({ ...membersQuery(orgId), enabled: member })
  const branches = useQuery({ ...branchesQuery(orgId), enabled: member })
  const me = members.data?.find((m) => m.userId === access.userId) ?? null

  return (
    <Page>
      <PageHeader title={t.staff.me.title} subtitle={t.staff.me.subtitle} />
      {!member ? (
        <EmptyState icon={<UserRound />} title={t.staff.me.title} message={t.staff.me.notMember} />
      ) : members.isPending || branches.isPending ? (
        <LoadingBlock label={t.app.loading} />
      ) : members.isError || branches.isError ? (
        <InlineBanner
          kind="error"
          message={errorText(members.error ?? branches.error)}
          action={{
            label: t.app.retry,
            onClick: () => {
              void members.refetch()
              void branches.refetch()
            },
          }}
        />
      ) : me ? (
        <ProfileForm key={me.id} orgId={orgId} member={me} branches={branches.data} />
      ) : (
        <EmptyState icon={<UserRound />} title={t.staff.me.title} message={t.staff.me.notMember} />
      )}
    </Page>
  )
}

function ProfileForm({ orgId, member, branches }: { orgId: string; member: Member; branches: readonly Branch[] }) {
  const update = useUpdateMember(orgId)
  const [title, setTitle] = useState(member.title ?? '')
  const [bio, setBio] = useState(member.bio ?? '')
  const [problems, setProblems] = useState<ProfileProblems>({})
  const [saved, setSaved] = useState(false)

  // Only the profile is ever sent from here — never role, name or clubs.
  const draft = { title, bio, displayName: member.displayName ?? '', role: member.role, scope: member.branchScope }
  const changes = profileChanges(member, member.branchScope, draft, false)
  const unchanged = Object.keys(changes).length === 0

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const next = profileProblems(draft, false)
    setProblems(next)
    if (Object.keys(next).length > 0 || unchanged) return
    update.mutate({ userId: member.userId, ...changes }, { onSuccess: () => setSaved(true) })
  }

  return (
    <div className="flex max-w-[640px] flex-col gap-8">
      <form className="flex flex-col gap-7 [--well:var(--surface)]" onSubmit={submit} noValidate>
        <MemberPhoto orgId={orgId} member={member} />
        <ProfileFields
          title={title}
          bio={bio}
          onTitle={(value) => {
            setTitle(value)
            setSaved(false)
            setProblems((prev) => ({ ...prev, title: undefined }))
          }}
          onBio={(value) => {
            setBio(value)
            setSaved(false)
            setProblems((prev) => ({ ...prev, bio: undefined }))
          }}
          problems={problems}
        />
        {update.isError ? <InlineBanner kind="error" message={errorText(update.error)} /> : null}
        {saved && unchanged ? <InlineBanner kind="done" message={t.staff.profile.saved} /> : null}
        <div className="flex justify-end">
          <Button type="submit" variant="primary" busy={update.isPending} disabled={unchanged}>
            {update.isPending ? t.app.saving : t.staff.me.save}
          </Button>
        </div>
      </form>
      <StandingReadOnly member={member} branches={branches} />
    </div>
  )
}
